import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiArrowRight, FiBox } from 'react-icons/fi';
import { getVendors } from '../services/api';
import StarRating from '../components/common/StarRating';
import PageTransition from '../components/common/PageTransition';
import './VendorsPage.css';

const picsum = (seed) => `https://picsum.photos/seed/${seed}/400/200`;


const CATEGORIES = ['All', 'Electronics', 'Fashion', 'Sports', 'Home & Garden', 'Books', 'Beauty', 'Automotive', 'Toys'];

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getVendors({ status: 'approved', limit: 100 });
      setVendors(data.vendors || []);
    } catch (err) {
      console.error('Vendors page error:', err?.response?.data || err.message);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const filtered = vendors.filter((v) => {
    const matchSearch = !search || v.storeName.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || v.category === category;
    return matchSearch && matchCat;
  });

  return (
    <PageTransition>
    <div className="vp-page">
      {/* Hero */}
      <section className="vp-hero">
        <div className="container vp-hero-inner">
          <h1 className="vp-hero-title">Explore Our Sellers</h1>
          <p className="vp-hero-sub">Discover unique stores from independent vendors across Pakistan</p>
          <div className="vp-search-bar">
            <FiSearch size={16} className="vp-search-icon" />
            <input
              className="vp-search-input"
              placeholder="Search stores…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </section>

      <div className="container vp-body">
        {/* Category chips */}
        <div className="vp-cats">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`vp-cat-chip ${category === c ? 'active' : ''}`}
              onClick={() => setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Count */}
        <p className="vp-count text-muted text-sm">
          {loading ? 'Loading…' : `${filtered.length} store${filtered.length !== 1 ? 's' : ''}`}
        </p>

        {loading ? (
          <div className="loading-center" style={{ padding: '4rem' }}>
            <div className="spinner" />
            <span>Loading stores…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '4rem' }}>
            <div className="empty-state-icon">🏪</div>
            <h3>No stores found</h3>
            <p>Try a different search or category.</p>
          </div>
        ) : (
          <div className="vp-grid">
            {filtered.map((vendor) => (
              <VendorCard key={vendor._id} vendor={vendor} />
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="vp-cta">
        <div className="container vp-cta-inner">
          <h2 className="vp-cta-title">Want to sell on fivora?</h2>
          <p className="vp-cta-sub">Join 1,000+ sellers. Keep 90% of every sale.</p>
          <Link to="/vendor/apply" className="btn btn-primary btn-lg">
            Start Selling <FiArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
    </PageTransition>
  );
}

function VendorCard({ vendor }) {
  const bannerSrc = vendor.banner || picsum(vendor._id);
  const logoInitial = vendor.storeName?.[0]?.toUpperCase() || 'V';

  return (
    <div className="vc-card card">
      {/* Banner */}
      <div className="vc-banner" style={{ backgroundImage: `url(${bannerSrc})` }}>
        {vendor.category && <span className="vc-category-chip">{vendor.category}</span>}
      </div>

      {/* Logo */}
      <div className="vc-logo-wrap">
        <div className="vc-logo">
          {vendor.logo
            ? <img src={vendor.logo} alt={vendor.storeName} />
            : <span>{logoInitial}</span>}
        </div>
      </div>

      {/* Info */}
      <div className="vc-info">
        <h3 className="vc-name">{vendor.storeName}</h3>
        {vendor.rating > 0 && (
          <div className="vc-rating">
            <StarRating rating={vendor.rating} totalReviews={vendor.totalReviews} size={12} />
          </div>
        )}
        {vendor.description && (
          <p className="vc-desc">{vendor.description}</p>
        )}
        <div className="vc-meta">
          <span className="vc-meta-item">
            <FiBox size={12} />
            {vendor.totalProducts ?? 0} products
          </span>
        </div>
      </div>

      <div className="vc-footer">
        <Link
          to={`/vendors/${vendor._id}`}
          className="btn btn-primary btn-sm btn-block"
        >
          Visit Store <FiArrowRight size={13} />
        </Link>
      </div>
    </div>
  );
}
